import React, { useState, useEffect } from 'react';
import { groupsAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiLock, FiPlus, FiUsers } from 'react-icons/fi';
import GroupModal from './GroupModal';
import './Settings.css';

const GroupManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const response = await groupsAPI.getAll();
      
      if (response.data.success) {
        setGroups(response.data.groups);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading groups:', err);
      setError(t('messages.error_load', 'Failed to load groups'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedGroup(null);
    setShowModal(true);
  };

  const handleEdit = (group) => {
    setSelectedGroup(group);
    setShowModal(true);
  };

  const handleDelete = async (group) => {
    if (group.user_count > 0) {
      alert(t('groups.has_users', `Cannot delete group "${group.group_name}" because it has ${group.user_count} assigned users.`));
      return;
    }

    if (!window.confirm(t('groups.confirm_delete', `Are you sure you want to delete group "${group.group_name}"?`))) {
      return;
    }
    
    try {
      const response = await groupsAPI.delete(group.group_id);
      if (response.data.success) {
        alert(t('messages.success_delete', 'Group deleted successfully'));
        loadGroups();
      }
    } catch (err) {
      alert(err.response?.data?.error || t('messages.error_delete', 'Failed to delete group'));
    }
  };

  const handleManagePermissions = (group) => {
    navigate(`/settings/permissions/${group.group_id}`);
  };

  const handleModalClose = (shouldReload) => {
    setShowModal(false);
    setSelectedGroup(null);
    if (shouldReload) {
      loadGroups();
    }
  };

  if (loading) {
    return <div className="loading">{t('loading', 'Loading groups...')}</div>;
  }

  if (error) {
    return (
      <div className="error">
        <p>{error}</p>
        <button onClick={loadGroups}>{t('retry', 'Retry')}</button>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div>
          <h2>{t('groups.title', 'Group Management')}</h2>
          <p className="settings-subtitle">{t('groups.subtitle', 'Manage user groups and roles')}</p>
        </div>
        <button onClick={handleCreate} className="btn-primary">
          <FiPlus size={16} style={{ marginRight: '6px' }} />
          {t('groups.create', 'Create Group')}
        </button>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>{t('groups.name', 'Group Name')}</th>
              <th>{t('groups.description', 'Description')}</th>
              <th>{t('company', 'Company')}</th>
              <th className="text-center">{t('users.title', 'Users')}</th>
              <th>{t('created_at', 'Created')}</th>
              <th>{t('actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.group_id}>
                <td className="username">{group.group_name}</td>
                <td>{group.description || '-'}</td>
                <td>{group.company_name || '-'}</td>
                <td className="text-center">
                  <span className="badge" style={{ 
                    background: '#f3f4f6', 
                    color: '#374151',
                    border: '1px solid #d1d5db'
                  }}>
                    <FiUsers size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    {group.user_count || 0}
                  </span>
                </td>
                <td>{new Date(group.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      onClick={() => handleEdit(group)}
                      className="btn-action btn-edit"
                      title={t('edit', 'Edit')}
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button 
                      onClick={() => handleManagePermissions(group)}
                      className="btn-action btn-view"
                      title={t('groups.permissions', 'Manage Permissions')}
                    >
                      <FiLock size={16} />
                    </button>
                    <button 
                      onClick={() => handleDelete(group)}
                      className="btn-action btn-delete"
                      title={t('delete', 'Delete')}
                      disabled={group.user_count > 0}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <GroupModal
          group={selectedGroup}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default GroupManagement;